/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";

import { useRef } from "react";

import { noop } from "lodash";
import { useFiltersHeightStore } from "@/stores/useFiltersHeightStore";
import { useMainBannerStore } from "@/stores/useMainBannerStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";
import MuiTable from "@mui/material/Table";

import { colors } from "@dotnet/telematics-design-system";

import {
  generateDesktopTableStyle,
  ROUTES,
  useMediaQueries,
  useScroll
} from "../../../../shared";
import useGeneralHeights from "../../../../shared/hooks/useGeneralHeights";
import useFilteredValues from "../../../Filters/hooks/useFilteredValues";
import { generateDesktopTableHeight } from "../../functions";
import TableBody from "../TableBody/TableBody";
import DesktopTableHeadActive from "./components/DesktopTableHeadActive";
import TableHead from "./components/TableHead";

interface IDesktopDetailsProps {
  route: ROUTES;
  onScroll?: boolean;
  hasTabFilters?: boolean;
  hasDetails: boolean;
  hasSearch?: boolean;
  hasTableView?: boolean;
  tableRowSwitch?: (rowData: any) => Promise<void>;
}

const DesktopTable = ({
  route,
  onScroll = true,
  hasTabFilters,
  hasDetails,
  hasSearch = true,
  hasTableView,
  tableRowSwitch
}: IDesktopDetailsProps) => {
  const { toMd, toLg } = useMediaQueries();
  const { scrollHorizontal, handleScrollHorizontal } = useScroll();

  const { filteredValues } = useFilteredValues();

  const loading = useTableStoreByRoute(route, state => state.loading);
  const hasSelectedRows = useTableStoreByRoute(
    route,
    state => state.selectedRows?.length > 0
  );

  const filtersHeightValue = useFiltersHeightStore(state => state.height);
  const mainBannerHeight = useMainBannerStore(state => state.height);

  // Scroll container: the virtualizer needs it to know what's visible
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    PAGE_HEADER_HEIGHT,
    NAVBAR_HEIGHT,
    TAB_FILTERS_HEIGHT,
    TABLE_TOOLBAR_HEIGHT,
    DESKTOP_TABLE_FOOTER_HEIGHT
  } = useGeneralHeights(mainBannerHeight);

  const companiesPage = route === ROUTES.Companies;

  const hasFilteredValues = filteredValues.length > 0;

  const desktopTableHeight = generateDesktopTableHeight({
    hasTabFilters,
    toMd,
    toLg,
    hasSearch,
    hasTableView,
    hasFilteredValues,
    companiesPage,
    filtersHeightValue,
    PAGE_HEADER_HEIGHT,
    NAVBAR_HEIGHT,
    TAB_FILTERS_HEIGHT,
    TABLE_TOOLBAR_HEIGHT,
    DESKTOP_TABLE_FOOTER_HEIGHT
  });

  return (
    <div
      ref={scrollRef}
      data-testid="desktop-table"
      onScroll={onScroll ? handleScrollHorizontal : noop}
      css={css({
        overflowY: "auto",
        overflowX: companiesPage ? "hidden" : "auto",
        height: desktopTableHeight,
        scrollbarWidth: "thin",
        backgroundColor: companiesPage ? `${colors.gray50}` : "unset"
      })}
    >
      {hasSelectedRows && <DesktopTableHeadActive route={route} />}
      <MuiTable
        stickyHeader
        css={css(
          generateDesktopTableStyle(colors, companiesPage, loading) as any
        )}
      >
        <TableHead
          route={route}
          scrollHorizontal={scrollHorizontal}
          companiesPage={companiesPage}
        />
        <TableBody
          route={route}
          scrollHorizontal={scrollHorizontal}
          hasDetails={hasDetails}
          tableRowSwitch={tableRowSwitch}
          scrollElementRef={scrollRef}
          virtualize={companiesPage}
        />
      </MuiTable>
    </div>
  );
};

export default DesktopTable;
